import { defineStore } from 'pinia';
import type { CategoryData } from '../../../stakeholdersMap/types/CategoryData.ts';
import type { StakeholderData } from '../../../stakeholdersMap/types/StakeholderData.ts';
import { StakeholderMapRequest } from '../services/StakeholderMapRequest';
import {StakeholderMapResponse} from "../services/StakeholderMapResponse.ts";
import {StakeholderPostRequest, StakeholderPostData} from "../services/StakeholderPostRequest.ts";
import { CreateCategoryData } from '../../../stakeholdersMap/types/CreateCategoryData.ts';

export const useStakeholderMapStore = (request: StakeholderMapRequest) => defineStore('stakeholderMapStore', {
  state: () => ({
    stakeholderMap: null as StakeholderMapResponse | null,
    categories: [] as CategoryData[],
    loading: false,
    error: null as string | null,
    currentMapId: null as string | null,
  }),
  
  actions: {
    /**
     * Fetches stakeholder map data (categories with their stakeholders)
     * @param mapId - The ID of the stakeholder map to fetch
     */
    async fetchStakeholderMap(mapId: string) {
      this.loading = true;
      this.error = null;
      this.currentMapId = mapId;
      
      try {
        this.stakeholderMap = await request.getStakeholderMap(mapId);

        this.categories = this.stakeholderMap.categories;
      } catch (err) {
        this.error = err instanceof Error ? err.message : 'Failed to fetch stakeholder map';
        console.error(this.error);
      } finally {
        this.loading = false;
      }
    },
    
    /**
     * Adds a new category
     * @param category - The category to add
     */
    async addCategory(category: CreateCategoryData) {
      if (!this.currentMapId) {
        throw new Error('No stakeholder map selected');
      }
      
      try {
        const categoryPostRequest: CreateCategoryData = {
          name: category.name
        };
        
        const response = await request.postCategory(this.currentMapId, categoryPostRequest);
        
        const newCategory: CategoryData = {
          name: category.name,
          stakeholdersData: [],
          id: response.id
        };
        console.log(newCategory);
        this.categories.push(newCategory);
      } catch (err) {
        this.error = err instanceof Error ? err.message : 'Failed to add category';
        console.error(this.error);
        throw err;
      }
    },
    
    /**
     * Updates an existing category
     * @param updatedCategory - The updated category data
     */
    async updateCategory(updatedCategory: CategoryData) {
      if (!this.currentMapId) {
        throw new Error('No stakeholder map selected');
      }
      
      try {
        await request.updateCategory(this.currentMapId, updatedCategory.id, updatedCategory);
        
        // Update local state with the input data
        const index = this.categories.findIndex(c => c.id === updatedCategory.id);
        if (index !== -1) {
          this.categories[index] = updatedCategory;
        }
      } catch (err) {
        this.error = err instanceof Error ? err.message : 'Failed to update category';
        console.error(this.error);
        throw err;
      }
    },
    
    /**
     * Deletes a category by ID
     * @param categoryId - The ID of the category to delete
     */
    async deleteCategory(categoryId: string) {
      if (!this.currentMapId) {
        throw new Error('No stakeholder map selected');
      }
      
      try {
        await request.deleteCategory(this.currentMapId, categoryId);
        
        // Update local state by removing the category
        const index = this.categories.findIndex(c => c.id === categoryId);
        if (index !== -1) {
          this.categories.splice(index, 1);
        }
      } catch (err) {
        this.error = err instanceof Error ? err.message : 'Failed to delete category';
        console.error(this.error);
        throw err;
      }
    },
    
    /**
     * Adds a stakeholder to a category
     * @param categoryId - The ID of the category to add the stakeholder to
     * @param stakeholder - The stakeholder data to add
     */
    async addStakeholderToCategory(categoryId: string, stakeholder: StakeholderData) {
      if (!this.currentMapId) {
        throw new Error('No stakeholder map selected');
      }
      
      try {
        const stakeholderPostData: StakeholderPostData = {
          label: stakeholder.text,
          dependency: stakeholder.dependency,
          penetration: stakeholder.penetration,
          maturity: stakeholder.maturity,
          trust: stakeholder.trust
        };
        
        const stakeholderPostRequest: StakeholderPostRequest = {
          stakeholder: stakeholderPostData,
          categoryId
        };
        
        const response = await request.postStakeholder(this.currentMapId, stakeholderPostRequest);
        
        // Update local state with the input data and server-generated ID
        const newStakeholder: StakeholderData = {
          ...stakeholder,
          id: response.id,
          categoryId
        };
        
        const category = this.categories.find(c => c.id === categoryId);
        if (category) {
          category.stakeholdersData.push(newStakeholder);
        }
      } catch (err) {
        this.error = err instanceof Error ? err.message : 'Failed to add stakeholder';
        console.error(this.error);
        throw err;
      }
    },
    
    /**
     * Updates a stakeholder in a category
     * @param stakeholder - The updated stakeholder data
     */
    async updateStakeholder(stakeholder: StakeholderData) {
      if (!this.currentMapId) {
        throw new Error('No stakeholder map selected');
      }
      
      try {
        const stakeholderPostData: StakeholderPostData = {
          label: stakeholder.text,
          dependency: stakeholder.dependency,
          penetration: stakeholder.penetration,
          maturity: stakeholder.maturity,
          trust: stakeholder.trust
        };
        
        const stakeholderPostRequest: StakeholderPostRequest = {
          stakeholder: stakeholderPostData,
          categoryId: stakeholder.categoryId
        };
        
        await request.updateStakeholder(this.currentMapId, stakeholder.id, stakeholderPostRequest);
        
        // Update local state with the input data
        const category = this.categories.find(c => c.id === stakeholder.categoryId);
        if (category) {
          const index = category.stakeholdersData.findIndex(s => s.id === stakeholder.id);
          if (index !== -1) {
            category.stakeholdersData[index] = stakeholder;
          }
        }
      } catch (err) {
        this.error = err instanceof Error ? err.message : 'Failed to update stakeholder';
        console.error(this.error);
        throw err;
      }
    },
    
    /**
     * Deletes a stakeholder by ID
     * @param categoryId - category ID where to find the stakeholder (kept for compatibility)
     * @param stakeholderId - The ID of the stakeholder to delete
     */
    async deleteStakeholder(_categoryId: string, stakeholderId: string) {
      if (!this.currentMapId) {
        throw new Error('No stakeholder map selected');
      }
      
      try {
        await request.deleteStakeholder(this.currentMapId, stakeholderId);
        
        // Update local state by removing the stakeholder
        for (const category of this.categories) {
          const index = category.stakeholdersData.findIndex(s => s.id === stakeholderId);
          if (index !== -1) {
            category.stakeholdersData.splice(index, 1);
            break;
          }
        }
      } catch (err) {
        this.error = err instanceof Error ? err.message : 'Failed to delete stakeholder';
        console.error(this.error);
        throw err;
      }
    },

    /**
     * Moves a stakeholder from one category to another
     * @param stakeholderId - The ID of the stakeholder to move
     * @param fromCategoryId - The ID of the current category
     * @param toCategoryId - The ID of the target category
     */
    async moveStakeholderToCategory(stakeholderId: string, fromCategoryId: string, toCategoryId: string) {
      if (!this.currentMapId) {
        throw new Error('No stakeholder map selected');
      }
      
      try {
        // Find the stakeholder to move
        const fromCategory = this.categories.find(c => c.id === fromCategoryId);
        if (!fromCategory) {
          throw new Error('Source category not found');
        }
        
        const stakeholder = fromCategory.stakeholdersData.find(s => s.id === stakeholderId);
        if (!stakeholder) {
          throw new Error('Stakeholder not found');
        }
        
        // Update the stakeholder with the new category ID
        const updatedStakeholder = { ...stakeholder, categoryId: toCategoryId };
        const stakeholderPostData: StakeholderPostData = {
          label: updatedStakeholder.text,
          dependency: updatedStakeholder.dependency,
          penetration: updatedStakeholder.penetration,
          maturity: updatedStakeholder.maturity,
          trust: updatedStakeholder.trust
        };
        
        const stakeholderPostRequest: StakeholderPostRequest = {
          stakeholder: stakeholderPostData,
          categoryId: toCategoryId
        };
        
        await request.updateStakeholder(this.currentMapId, stakeholderId, stakeholderPostRequest);
        
        // Update local state by moving the stakeholder
        const stakeholderIndex = fromCategory.stakeholdersData.findIndex(s => s.id === stakeholderId);
        if (stakeholderIndex !== -1) {
          const movedStakeholder = fromCategory.stakeholdersData[stakeholderIndex];
          // Remove from current category
          fromCategory.stakeholdersData.splice(stakeholderIndex, 1);
          // Add to new category with updated categoryId
          const toCategory = this.categories.find(c => c.id === toCategoryId);
          if (toCategory) {
            toCategory.stakeholdersData.push({ ...movedStakeholder, categoryId: toCategoryId });
          }
        }
      } catch (err) {
        this.error = err instanceof Error ? err.message : 'Failed to move stakeholder';
        console.error(this.error);
        throw err;
      }
    },
    
    /**
     * Clears all categories and stakeholders
     */
    clearStakeholderMap() {
      this.categories = [];
      this.currentMapId = null;
    },

    /**
     * Generates a unique ID for a new stakeholder
     * @returns A unique ID string
     */
    generateStakeholderId(): string {
      return 'stakeholder-' + Math.random().toString(36).slice(2, 9);
    },

    /**
     * Creates a new stakeholder with default values
     * @param categoryId - The ID of the category to add the stakeholder to
     * @returns The new stakeholder data structure
     */
    createDefaultStakeholder(categoryId: string): StakeholderData {
      return {
        id: this.generateStakeholderId(),
        text: 'New Stakeholder',
        categoryId,
        dependency: 1,
        penetration: 1,
        maturity: 4,
        trust: 4
      };
    },

    /**
     * Creates a default category data structure
     * @returns A new category with default name
     */
    createDefaultCategory(): CreateCategoryData {
      return {
        name: 'New Category'
      };
    }
  },
  
  getters: {

    getCategoryById: (state) => (id: string) => {
      return state.categories.find(c => c.id === id);
    },

    getAllCategories: (state) => {
      return state.categories;
    },

    getStakeholderById: (state) => (categoryId: string, stakeholderId: string) => {
      const cat = state.categories.find(c => c.id === categoryId);
      if(cat) {
        return cat.stakeholdersData.find(s => s.id === stakeholderId);
      }
    },

    getAllStakeholders: (state) => {
      return state.categories.flatMap(c => c.stakeholdersData.map(s => ({
        ...s,
        categoryName: c.name
      })));
    },

    getStakeholdersByCategory: (state) => (categoryId: string) => {
      const category = state.categories.find(c => c.id === categoryId);
      return category ? category.stakeholdersData : [];
    },

    getStakeholdersCountByCategory: (state) => (categoryId: string) => {
      const category = state.categories.find(c => c.id === categoryId);
      return category ? category.stakeholdersData.length : 0;
    },

    isLoading: (state) => state.loading,

    hasError: (state) => state.error !== null,

    getCurrentMapId: (state) => state.currentMapId
  }
});